import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Check, BookOpen } from 'lucide-react';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';
import { useDialogA11y } from '../ui/useDialogA11y';

interface Course {
  id: string;
  name: string;
}

interface Module {
  id: string;
  name: string;
  order_index: number;
  course_id: string;
}

interface MultipleAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedMaterialIds: string[];
  materialTitles: string[];
  onCourseChange?: (courseId: string) => void;
}

export function MultipleAssignModal({
  isOpen,
  onClose,
  onSuccess,
  selectedMaterialIds,
  materialTitles,
  onCourseChange,
}: MultipleAssignModalProps) {
  const confirm = useConfirm();
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      loadCourses();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCourseId) {
      loadModules(selectedCourseId);
    } else {
      setModules([]);
      setSelectedModuleId('');
    }
  }, [selectedCourseId]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async (courseId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('id, name, order_index, course_id')
        .eq('course_id', courseId)
        .order('order_index');

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModuleId || selectedMaterialIds.length === 0) {
      toast.error('Selecciona un módulo');
      return;
    }

    setSubmitting(true);
    try {
      const assignments = selectedMaterialIds.map((materialId) => ({
        module_id: selectedModuleId,
        material_id: materialId,
      }));

      const { error } = await supabase
        .from('module_materials')
        .upsert(assignments, { onConflict: 'module_id,material_id' });

      if (error) throw error;

      toast.success(`${selectedMaterialIds.length} material${selectedMaterialIds.length !== 1 ? 'es' : ''} asignado${selectedMaterialIds.length !== 1 ? 's' : ''} al módulo`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error assigning materials:', error);
      toast.error('Error al asignar los materiales: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignFromCourse = async () => {
    if (!selectedCourseId || selectedMaterialIds.length === 0) {
      toast.error('Selecciona un curso');
      return;
    }

    const ok = await confirm({
      title: 'Desasignar materiales',
      description: `Se desasignarán ${selectedMaterialIds.length} material${selectedMaterialIds.length !== 1 ? 'es' : ''} de TODOS los módulos del curso seleccionado. Esta acción no se puede deshacer.`,
      confirmText: 'Desasignar',
      tone: 'danger',
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      const moduleIds = modules.map(m => m.id);

      const { error } = await supabase
        .from('module_materials')
        .delete()
        .in('material_id', selectedMaterialIds)
        .in('module_id', moduleIds);

      if (error) throw error;

      toast.success(`${selectedMaterialIds.length} material${selectedMaterialIds.length !== 1 ? 'es' : ''} desasignado${selectedMaterialIds.length !== 1 ? 's' : ''} del curso`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error unassigning materials:', error);
      toast.error('Error al desasignar los materiales: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-multipleassign"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/[0.06]"
      >
        <div className="sticky top-0 glass-surface border-b border-white/[0.06] p-6 flex justify-between items-start z-10">
          <div>
            <h2 id="modal-title-multipleassign" className="text-display text-2xl font-bold text-white mb-2">Asignación Múltiple</h2>
            <p className="text-muted text-sm">
              Asignando {selectedMaterialIds.length} material{selectedMaterialIds.length !== 1 ? 'es' : ''} a un módulo
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
            <h3 className="text-sm font-semibold text-muted mb-3">Materiales seleccionados:</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {materialTitles.map((title, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-cubus-lime flex-shrink-0" />
                  <span className="truncate">{title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Seleccionar Curso *
              </label>
              <div className="relative">
                <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                <select
                  value={selectedCourseId}
                  onChange={(e) => {
                    const courseId = e.target.value;
                    setSelectedCourseId(courseId);
                    setSelectedModuleId('');
                    if (onCourseChange) {
                      onCourseChange(courseId);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime appearance-none cursor-pointer"
                  required
                  disabled={loading}
                >
                  <option value="">Selecciona un curso...</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedCourseId && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Seleccionar Módulo *
                </label>
                <div className="relative">
                  <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                  <select
                    value={selectedModuleId}
                    onChange={(e) => setSelectedModuleId(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime appearance-none cursor-pointer"
                    required
                    disabled={loading || modules.length === 0}
                  >
                    <option value="">Selecciona un módulo...</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.id}>
                        Módulo {module.order_index}: {module.name}
                      </option>
                    ))}
                  </select>
                </div>
                {modules.length === 0 && !loading && (
                  <p className="text-sm text-muted mt-2">
                    Este curso no tiene módulos todavía
                  </p>
                )}
              </div>
            )}
          </div>

          {selectedCourseId && modules.length > 0 && (
            <div className="pb-4 border-t border-white/[0.08] pt-4">
              <button
                type="button"
                onClick={handleUnassignFromCourse}
                disabled={submitting}
                className="w-full px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Desasignar del Curso Completo
              </button>
              <p className="text-xs text-subtle mt-2 text-center">
                Esto eliminará los {selectedMaterialIds.length} materiales seleccionados de todos los módulos del curso
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-white/[0.08]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedModuleId}
              className="flex-1 px-4 py-3 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Asignando...' : `Asignar ${selectedMaterialIds.length} Material${selectedMaterialIds.length !== 1 ? 'es' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
